import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { apprenticeApi } from '../api/apprentices';
import { trackingApi } from '../api/tracking';
import { Apprentice, OTJRecord } from '../types';
import { format } from 'date-fns';
import axios from 'axios';

export const TrackingPage: React.FC = () => {
  const { user, userRole } = useAuth();
  const [apprentices, setApprentices] = useState<Apprentice[]>([]);
  const [selectedApprenticeId, setSelectedApprenticeId] = useState<string>('');
  const [otjRecords, setOtjRecords] = useState<OTJRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [verifyingId, setVerifyingId] = useState<string | null>(null);

  const canVerify = user?.roles?.some(role => 
    role.includes('Administrator') || role.includes('Instructor')
  );

  const loadApprentices = useCallback(async () => {
    if (!user?.tenant_id) return;
    
    try {
      // For instructors, only load their caseload
      const instructorEmail = userRole === 'instructor' ? user.email : undefined;
      const response = await apprenticeApi.getAll(user.tenant_id, undefined, instructorEmail);
      setApprentices(response.data);
    } catch (err) {
      console.error('Failed to load apprentices:', err);
    }
  }, [user?.tenant_id, user?.user_id, userRole]);

  const loadOTJRecords = useCallback(async (apprenticeId: string) => {
    if (!user?.tenant_id || !apprenticeId) return;
    
    try {
      setLoading(true);
      setError(null);
      const response = await trackingApi.getOTJRecords(user.tenant_id, apprenticeId);
      setOtjRecords(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load OTJ records');
      setOtjRecords([]);
    } finally {
      setLoading(false);
    }
  }, [user?.tenant_id]);

  const handleVerify = async (recordId: string, status: 'approved' | 'rejected') => {
    if (!user?.tenant_id) return;

    try {
      setVerifyingId(recordId);
      await trackingApi.verifyOTJ(user.tenant_id, recordId, user.user_id, status);
      
      // Reload records to get updated status
      if (selectedApprenticeId) {
        await loadOTJRecords(selectedApprenticeId);
      }
    } catch (err: any) {
      console.error('Failed to verify record:', err);
      alert(err.response?.data?.message || 'Failed to verify record');
    } finally {
      setVerifyingId(null);
    }
  };

  useEffect(() => {
    loadApprentices();
  }, [loadApprentices]);

  useEffect(() => {
    if (selectedApprenticeId) {
      loadOTJRecords(selectedApprenticeId);
    } else {
      setOtjRecords([]);
    }
  }, [selectedApprenticeId, loadOTJRecords]);

  const selectedApprentice = apprentices.find(a => a.id === selectedApprenticeId);

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {userRole === 'instructor' ? 'My Caseload - OTJ Tracking' : 'OTJ Tracking'}
        </h1>
        <p className="text-gray-600">
          {userRole === 'instructor' 
            ? `Verify and track off-the-job training hours for your ${apprentices.length} apprentices`
            : 'Verify and track off-the-job training hours for apprentices'}
        </p>
      </div>

      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Select Apprentice
        </label>
        <select
          value={selectedApprenticeId}
          onChange={(e) => setSelectedApprenticeId(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">-- Select an apprentice --</option>
          {apprentices.map(apprentice => (
            <option key={apprentice.id} value={apprentice.id}>
              {apprentice.first_name} {apprentice.last_name} - {apprentice.email}
            </option>
          ))}
        </select>
        {apprentices.length === 0 && (
          <p className="mt-2 text-sm text-gray-500">
            {userRole === 'instructor' 
              ? 'No apprentices assigned to you yet. Contact an administrator to assign apprentices to your caseload.'
              : 'No apprentices in the system yet.'}
          </p>
        )}
      </div>

      {selectedApprenticeId && selectedApprentice && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-semibold">
                {selectedApprentice.first_name} {selectedApprentice.last_name}
              </h2>
              <p className="text-sm text-gray-600">{selectedApprentice.email}</p>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-500">OTJ Progress</div>
              <div className="text-2xl font-bold text-blue-600">
                {Math.round(selectedApprentice.otj_percentage)}%
              </div>
            </div>
          </div>
          <Link
            to={`/apprentices/${selectedApprentice.id}`}
            className="text-blue-600 hover:text-blue-700 text-sm font-medium"
          >
            View Full Profile →
          </Link>
        </div>
      )}

      {loading && (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-gray-600">Loading OTJ records...</p>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {!loading && selectedApprenticeId && otjRecords.length === 0 && (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-gray-600">No OTJ records found for this apprentice.</p>
        </div>
      )}

      {!loading && otjRecords.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold">OTJ Records</h3>
          </div>
          <div className="divide-y divide-gray-200">
            {otjRecords.map((record) => (
              <div key={record.id} className="p-6 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="font-semibold text-gray-900">{record.activity_type}</h4>
                      <span
                        className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          record.verification_status === 'approved'
                            ? 'bg-green-100 text-green-800'
                            : record.verification_status === 'rejected'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}
                      >
                        {record.verification_status}
                      </span>
                    </div>
                    <p className="text-gray-600 text-sm mb-2">{record.description}</p>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span>������ {format(new Date(record.activity_date), 'dd/MM/yyyy')}</span>
                      <span>⏱️ {record.hours} hours</span>
                    </div>
                    {record.verified_at && (
                      <p className="text-xs text-gray-500 mt-2">
                        Verified on {format(new Date(record.verified_at), 'dd/MM/yyyy HH:mm')}
                      </p>
                    )}
                  </div>
                  
                  {canVerify && record.verification_status === 'pending' && (
                    <div className="flex gap-2 ml-4">
                      <button
                        onClick={() => handleVerify(record.id, 'approved')}
                        disabled={verifyingId === record.id}
                        className="px-3 py-1 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 disabled:bg-gray-400 transition"
                      >
                        {verifyingId === record.id ? 'Processing...' : 'Approve'}
                      </button>
                      <button
                        onClick={() => handleVerify(record.id, 'rejected')}
                        disabled={verifyingId === record.id}
                        className="px-3 py-1 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 disabled:bg-gray-400 transition"
                      >
                        Reject
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
